/**
 * The Timeline's commit overlay, drawn (roadmap D180): a lane's commit
 * markers, the legend of stages beside the runs' legend, the hover card and
 * the glyph of a lane the overlay adds. The layout and the words are in
 * ./timelineCommitsModel.ts; ./useCommitOverlay.tsx puts them together for
 * WorkTimeline.tsx, which places them with a few lines.
 */
import './timelineCommits.css';

import { ArrowUpRightFromSquare, FaceRobot, Folder, Gear, Person, PersonGear, Xmark } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { type KeyboardEvent, type PointerEvent, useRef, useState } from 'react';

import { useDialogFocus } from '../../workbench/useDialogFocus.ts';
import { Hint } from '../ui/Hint.tsx';
import { clusterLabel, type CommitLane, type CommitLegendItem, type CommitTipText, MARKER_R as R, type MarkerCluster, type MarkerShape, markerTone, STAGE_SHAPE, type TimelineCommit } from './timelineCommitsModel.ts';
import type { OpenResult } from './timelineOpen.tsx';

/** One marker: the stage's shape in the plan's colour, on a ring of the card's ground. */
function MarkerMark({ shape, x, y }: { shape: MarkerShape; x: number; y: number }) {
  const r = shape === 'check' ? R + 0.75 : R;
  return (
    <>
      <circle className="wtl-commit__halo" cx={x} cy={y} r={r + 2} />
      <circle className="wtl-commit__mark" cx={x} cy={y} r={shape === 'hollow' || shape === 'dashed' ? r - 0.6 : r} />
      {shape === 'check' ? <path className="wtl-commit__tick" d={`M${x - 2.4},${y + 0.1} L${x - 0.7},${y + 1.8} L${x + 2.5},${y - 1.7}`} /> : null}
    </>
  );
}

export interface CommitMarkersProps {
  clusters: readonly MarkerCluster[];
  /** Where the markers sit (px from the lane's top): workTimelineModel `commitMarkerY`. */
  y: number;
  mainBranch?: (rootId: string) => string | undefined;
  hovered?: string;
  onHover: (cluster: MarkerCluster, e: PointerEvent<SVGGElement>) => void;
  onLeave: () => void;
  onOpen: (cluster: MarkerCluster) => void;
}

/** A lane's markers, inside its SVG: each one a button. */
export function CommitMarkers({ clusters, y, mainBranch, hovered, onHover, onLeave, onOpen }: CommitMarkersProps) {
  return (
    <g data-wtl-commits={clusters.length}>
      {clusters.map((cl) => {
        const shape = STAGE_SHAPE[cl.lead.stage];
        const tone = markerTone(cl.lead);
        const onKeyDown = (e: KeyboardEvent<SVGGElement>) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          onOpen(cl);
        };
        return (
          <g
            key={cl.key}
            className="wtl-commit"
            role="button"
            tabIndex={0}
            aria-label={clusterLabel(cl, mainBranch?.(cl.lead.rootId))}
            data-shape={shape}
            data-stage={cl.lead.stage}
            data-tone={tone}
            data-hovered={hovered === cl.key || undefined}
            data-wtl-commit={cl.lead.hash}
            onPointerEnter={(e) => onHover(cl, e)}
            onPointerMove={(e) => onHover(cl, e)}
            onPointerLeave={onLeave}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(cl);
            }}
            onKeyDown={onKeyDown}
          >
            {/* A bigger target than the mark. */}
            <circle cx={cl.x} cy={y} r={R + 5} fill="transparent" />
            <MarkerMark shape={shape} x={cl.x} y={y} />
            {cl.commits.length > 1 ? (
              <text className="wtl-commit__count" x={cl.x + R + 3} y={y - R + 1}>
                {cl.commits.length}
              </text>
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

/** A small marker for the legend and the hover card. */
export function CommitSwatch({ shape, tone }: { shape: MarkerShape; tone?: number }) {
  return (
    <svg className="wtl-commit wtl-commit-swatch" data-shape={shape} data-tone={tone} width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <MarkerMark shape={shape} x={7} y={7} />
    </svg>
  );
}

/** The stages present, beside the runs' legend: one legend area. */
export function CommitLegend({ items }: { items: readonly CommitLegendItem[] }) {
  if (!items.length) return null;
  return (
    <ul className="wtl-legend" aria-label="Commits" data-wtl-commit-legend>
      {items.map((it) => (
        <li key={it.stage} data-wtl-commit-legend-item={it.stage}>
          <CommitSwatch shape={it.shape} /> {it.label}
        </li>
      ))}
    </ul>
  );
}

/** The hover card: what it was, who, the task and plan, the stage in words, when. */
export function CommitTip({ tips, more, left, top }: { tips: readonly (CommitTipText & { shape: MarkerShape; tone?: number })[]; more: number; left: number; top: number }) {
  return (
    <div className="wtl-tip" style={{ left, top }} role="tooltip" data-wtl-commit-tip>
      {tips.map((t, i) => (
        <div key={i} className={i ? 'mt-1.5 border-t border-separator pt-1.5' : undefined}>
          <div className="font-semibold text-foreground">{t.title}</div>
          <div className="text-muted">{t.who}</div>
          {t.work ? <div className="text-muted">{t.work}</div> : null}
          <div className="wtl-commit-tip__stage">
            <CommitSwatch shape={t.shape} tone={t.tone} />
            {t.stage}
            {t.remote ? <span className="font-normal text-muted">· {t.remote}</span> : null}
          </div>
          <div className="tabular-nums text-muted">{t.when}</div>
        </div>
      ))}
      {more > 0 ? <div className="mt-1 text-muted">and {more} more — zoom in to tell them apart</div> : null}
      <div className="mt-1 text-muted">Click for the way to it on GitHub or on the board</div>
    </div>
  );
}

/** At most this many commits of a cluster are listed in its card. */
const DETAILS_MAX = 5;

/**
 * A marker's card, on a click or Enter: each commit (newest first) with the
 * way into it — Source Control on the commit, or its task on the board.
 * Console: the commit opens on GitHub, where the project's history is read from.
 */
export function CommitDetails({ cluster, tipOf, left, top, onOpenCommit, onOpenTask, onClose }: { cluster: MarkerCluster; tipOf: (c: TimelineCommit) => CommitTipText; left: number; top: number; onOpenCommit: (c: TimelineCommit) => Promise<OpenResult>; onOpenTask: (c: TimelineCommit) => Promise<OpenResult>; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [note, setNote] = useState<string | undefined>(undefined);
  useDialogFocus(ref, { onEscape: onClose });
  const act = (p: Promise<OpenResult>) => void p.then((r) => setNote(r.ok ? undefined : r.message));
  const newest = [...cluster.commits].reverse();
  const shown = newest.slice(0, DETAILS_MAX);
  return (
    <div ref={ref} className="wtl-details" style={{ left, top }} role="dialog" aria-label={cluster.commits.length === 1 ? `Commit: ${cluster.lead.subject}` : `${cluster.commits.length} commits`} tabIndex={-1} data-wtl-commit-details={cluster.lead.hash}>
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-foreground">{cluster.commits.length === 1 ? 'Commit' : `${cluster.commits.length} commits`}</span>
        <Hint text="Close (Esc)">
          <Button size="sm" variant="ghost" isIconOnly aria-label="Close the details" className="-mr-1 -mt-1" onPress={onClose}>
            <Xmark className="size-3.5" />
          </Button>
        </Hint>
      </div>
      {shown.map((c) => {
        const t = tipOf(c);
        return (
          <div key={c.hash} className="flex flex-col gap-1 border-t border-separator pt-2 text-xs first-of-type:border-t-0 first-of-type:pt-0" data-wtl-commit-row={c.hash}>
            <span className="font-medium text-foreground">{t.title}</span>
            <span className="text-muted">{t.who}</span>
            {t.work ? <span className="text-muted">{t.work}</span> : null}
            <span className="wtl-commit-tip__stage">
              <CommitSwatch shape={STAGE_SHAPE[c.stage]} tone={markerTone(c)} />
              {t.stage}
              {t.remote ? <span className="font-normal text-muted">· {t.remote}</span> : null}
            </span>
            <span className="tabular-nums text-muted">{t.when}</span>
            <span className="flex flex-wrap gap-1.5 pt-0.5">
              <Button size="sm" variant="secondary" onPress={() => act(onOpenCommit(c))} data-wtl-open-commit>
                <ArrowUpRightFromSquare className="size-3" /> Open on GitHub
              </Button>
              {c.task ? (
                <Button size="sm" variant="ghost" onPress={() => act(onOpenTask(c))} data-wtl-open-commit-task>
                  <ArrowUpRightFromSquare className="size-3" /> Open task
                </Button>
              ) : null}
            </span>
          </div>
        );
      })}
      {newest.length > shown.length ? <p className="m-0 text-xs text-muted">and {newest.length - shown.length} more — zoom in to tell them apart.</p> : null}
      {note ? (
        <p className="m-0 text-xs text-danger" role="alert">
          {note}
        </p>
      ) : null}
    </div>
  );
}

/** The glyph of a lane the overlay adds. */
export function CommitLaneGlyph({ lane }: { lane: CommitLane }) {
  const title = lane.commitGroup === 'outside-ai' ? 'An AI tool outside the workbench' : lane.commitGroup === 'person' ? 'A person' : lane.commitGroup === 'workbench' ? "The workbench's own housekeeping" : lane.commitGroup === 'repository' ? 'Repository' : 'Agent';
  return (
    <span className="wtl-lane__glyph" title={title} aria-hidden="true">
      {lane.commitGroup === 'outside-ai' ? <FaceRobot /> : lane.commitGroup === 'person' ? <Person /> : lane.commitGroup === 'workbench' ? <Gear /> : lane.commitGroup === 'repository' ? <Folder /> : <PersonGear />}
    </span>
  );
}
